import { ApiError, LOCALE_BCP47, type Locale, SUPPORTED_LOCALES } from '@staiter/shared';
// TODO(S1): переехать на shared api-contracts + typed client-builder из packages/shared.
import { z } from 'zod';
import { captureShapeMismatch } from '~/lib/sentry/capture';
import { TAB_CLIENT_ID } from '~/lib/tasks/sse/client-id';
import { getActiveEntityHeaders } from '~/store/active-entity';
import { isEntityForbidden } from './entity-forbidden';

/**
 * FM-S-multi-4-2 — allowlist endpoints без X-Active-Entity inject
 * (super-admin / auth / health). Matched через startsWith после API_BASE strip.
 */
const ACTIVE_ENTITY_ALLOWLIST = [
  '/auth/login',
  '/auth/refresh',
  '/auth/accept-invite',
  '/auth/me',
  '/admin/holdings',
  '/healthz',
  '/readyz',
  '/telemetry/',
];

function isAllowlistedFromHeader(path: string): boolean {
  return ACTIVE_ENTITY_ALLOWLIST.some((p) => path.startsWith(p));
}

const API_BASE = '/api/v1';

/**
 * F-A.8 — читает locale (из JWT claim или saved localStorage) без React-hook.
 * Используется rawRequest для Accept-Language header.
 */
function readCurrentLocale(): Locale {
  try {
    const saved = localStorage.getItem('staiter.locale');
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    // ignore
  }
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language.split('-')[0];
    if (nav && (SUPPORTED_LOCALES as readonly string[]).includes(nav)) return nav as Locale;
  }
  return 'ru';
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retry_after?: number;
  readonly details?: Record<string, unknown>;
  /**
   * F5 / B10.7 — trace_id из X-Trace-Id response header (контракт с backend B10.7).
   * Fallback — error.trace_id в body. Header надёжнее: доступен даже при network-fail.
   */
  readonly traceId?: string;

  constructor(
    status: number,
    payload: z.infer<typeof ApiError>,
    traceId: string | undefined = undefined,
  ) {
    super(payload.message);
    this.status = status;
    this.code = payload.code;
    if (payload.retry_after !== undefined) this.retry_after = payload.retry_after;
    if (payload.details !== undefined) this.details = payload.details;
    const trace = traceId ?? payload.trace_id;
    if (trace) this.traceId = trace;
  }
}

/**
 * S-FIX-2-FE.4.2 — ошибка несовместимого ответа сервера (Zod safeParse fail).
 *
 * Бросается из `apiRequest` при schema mismatch (raw data не подходит под Zod).
 * Раньше в этой ситуации `client.ts` тихо возвращал raw data → JSX крашился без
 * trace. Теперь — явный throw, route-level fallback (RouteErrorState) показывает
 * trace_id + локализованное описание.
 *
 * Удаление маскирующего fallback включается через `RequestOptions.tolerant: false`
 * (default).
 */
export class ApiSchemaError extends Error {
  readonly path: string;
  readonly method: string;
  readonly issues: ReadonlyArray<{ path: ReadonlyArray<string | number>; message: string }>;
  readonly traceId?: string;

  constructor(opts: {
    path: string;
    method: string;
    issues: ReadonlyArray<{ path: ReadonlyArray<string | number>; message: string }>;
    traceId?: string;
  }) {
    super(`Schema mismatch on ${opts.method} ${opts.path}`);
    this.name = 'ApiSchemaError';
    this.path = opts.path;
    this.method = opts.method;
    this.issues = opts.issues;
    if (opts.traceId) this.traceId = opts.traceId;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  /**
   * S-FIX-2-FE.4.1 — opt-in tolerant fallback. По умолчанию false (strict).
   * При true — при Zod-mismatch возвращаем raw `data as T` (как до S-FIX-2),
   * без выброса `ApiSchemaError`. Использовать только в местах где shape
   * заведомо может дрейфовать (legacy/beta endpoints).
   * Note: TEMPORARY run-adapter был удалён после S-FIX-2-BE.8.5 (migration 0026).
   */
  tolerant?: boolean;
  /**
   * U0.4 / W14.1 — если true, клиент автоматически выставит заголовок
   * `Idempotency-Key: <uuid>` (crypto.randomUUID). Backend кэширует ответ в Redis
   * (W14) и на повторе с тем же ключом возвращает тот же результат.
   *
   * Включать на mutation-call-ах, где повторный вызов может вызвать побочку
   * (WO signal, job-card CRUD, bulk-action, WO create).
   */
  idempotent?: boolean;
  /**
   * U0.4 — явный Idempotency-Key (переопределяет auto-gen). Используется mutation
   * wrapper (useIdempotentMutation) чтобы retry одной mutation шёл с тем же key.
   */
  idempotencyKey?: string;
};

let accessToken: string | null = null;
let refreshToken: string | null = null;

export const tokenStorage = {
  load(): void {
    try {
      accessToken = localStorage.getItem('staiter.access_token');
      refreshToken = localStorage.getItem('staiter.refresh_token');
    } catch {
      /* ignore */
    }
  },
  set(access: string, refresh: string): void {
    accessToken = access;
    refreshToken = refresh;
    try {
      localStorage.setItem('staiter.access_token', access);
      localStorage.setItem('staiter.refresh_token', refresh);
    } catch {
      /* ignore */
    }
  },
  clear(): void {
    accessToken = null;
    refreshToken = null;
    try {
      localStorage.removeItem('staiter.access_token');
      localStorage.removeItem('staiter.refresh_token');
    } catch {
      /* ignore */
    }
  },
  getAccess(): string | null {
    return accessToken;
  },
  getRefresh(): string | null {
    return refreshToken;
  },
};

tokenStorage.load();

// Кросс-таб синхронизация: `storage` приходит в ОСТАЛЬНЫЕ вкладки при ротации
// или логауте. Без подхвата вторая вкладка продолжала слать устаревший refresh —
// backend видел replay и отзывал всю family, разлогинивая обе вкладки.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === 'staiter.access_token' || e.key === 'staiter.refresh_token') {
      tokenStorage.load();
    }
  });
}

/**
 * E-FE.2 — tolerant parse error envelope. Если backend прислал shape mismatch
 * (legacy enum / missing fields), всё равно формируем ApiRequestError с code/trace.
 *
 * Вынесено из `rawRequest` (пак `tails-closeout`, F4 / CIA-F26): тем же разбором
 * пользуется поток чата (`streamChat`), который ходит мимо `apiRequest` и до правки
 * бросал `Error('Chat stream failed: 403')` — пузырь ошибки показывал этот текст
 * вместо локализованного `m.error_by_code`.
 */
export async function apiErrorFromResponse(
  response: Response,
  traceId: string | undefined = response.headers.get('x-trace-id') ?? undefined,
): Promise<ApiRequestError> {
  let parsed: z.infer<typeof ApiError>;
  try {
    const raw = (await response.json()) as unknown;
    const envelope =
      raw && typeof raw === 'object' && 'error' in raw ? (raw as { error: unknown }).error : raw;
    const safe = ApiError.safeParse(envelope);
    if (safe.success) {
      parsed = safe.data;
    } else {
      captureShapeMismatch('ApiError', safe.error, envelope);
      const fallbackCode =
        envelope && typeof envelope === 'object' && 'code' in envelope
          ? String((envelope as { code: unknown }).code)
          : 'server.internal';
      parsed = { code: fallbackCode };
    }
  } catch {
    parsed = { code: 'server.internal' };
  }
  return new ApiRequestError(response.status, parsed, traceId);
}

async function rawRequest<T>(
  path: string,
  // biome-ignore lint/suspicious/noExplicitAny: Zod's ZodType input type is any by design
  schema: z.ZodType<T, z.ZodTypeDef, any>,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // F-A.8 — Accept-Language для backend locale-detect middleware.
    'Accept-Language': LOCALE_BCP47[readCurrentLocale()],
    ...(options.headers ?? {}),
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!options.skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  /**
   * work-tasks-performance F21 — кто сделал правку, с точностью до вкладки.
   *
   * Заголовок уходит только с мутациями: у GET автора правки нет, а лишний
   * заголовок на каждом чтении — лишний повод для preflight. Бэкенд кладёт его в
   * SSE-событие (`origin_client_id`, B13), и стрим этой вкладки по нему узнаёт
   * СВОЁ эхо: без фильтра каждая правка возвращалась событием и вызывала второй
   * рефетч списка поверх уже применённого оптимистика (нарушение I3).
   */
  if ((options.method ?? 'GET') !== 'GET' && !headers['X-Client-Id']) {
    headers['X-Client-Id'] = TAB_CLIENT_ID;
  }

  // FM-S-multi-4-2 — auto-inject X-Active-Entity / X-Active-Scope.
  if (!isAllowlistedFromHeader(path)) {
    const tenancyHeaders = getActiveEntityHeaders();
    for (const [k, v] of Object.entries(tenancyHeaders)) {
      if (!headers[k]) headers[k] = v;
    }
  }
  // U0.4 / W14.1 — Idempotency-Key. Приоритет: explicit key (mutation wrapper) > auto-gen.
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  } else if (options.idempotent && !headers['Idempotency-Key']) {
    headers['Idempotency-Key'] =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }

  const response = await fetch(`${API_BASE}${path}`, init);
  // B10.7 — X-Trace-Id устойчивее body-trace_id: доступен даже при network-fail / 500 HTML.
  const traceId = response.headers.get('x-trace-id') ?? undefined;

  if (!response.ok) {
    throw await apiErrorFromResponse(response, traceId);
  }

  if (response.status === 204) {
    return schema.parse(undefined as unknown);
  }

  const data = (await response.json()) as unknown;
  // E-FE.2 — tolerant runtime shape check: strict parse only в CI (contract tests).
  // Runtime — safeParse, при mismatch → Sentry + возвращаем raw как есть (best-effort render).
  const safeResult = schema.safeParse(data);
  if (safeResult.success) {
    return safeResult.data;
  }

  captureShapeMismatch(`response:${init.method ?? 'GET'} ${path}`, safeResult.error, data);

  // F11 — ZodError telemetry: fire-and-forget POST в backend B10.3.
  void fetch(`${API_BASE}/telemetry/zod-error`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: path,
      method: init.method ?? 'GET',
      issues: safeResult.error.issues.slice(0, 10),
      trace_id: traceId,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      severity: 'warn',
    }),
  }).catch(() => {
    // double-fail guard; не нужен сигнал
  });

  // S-FIX-2-FE.4.1: strict mode default. Прежняя `return data as T` маскировала
  // backend bugs и приводила к JSX crashes без trace — теперь явный ApiSchemaError
  // с trace_id и path; route-level RouteErrorState показывает локализованное
  // сообщение пользователю. Opt-in tolerant fallback через `options.tolerant`.
  if (options.tolerant === true) {
    return data as T;
  }
  throw new ApiSchemaError({
    path,
    method: options.method ?? 'GET',
    issues: safeResult.error.issues.map((i) => ({ path: i.path, message: i.message })),
    ...(traceId ? { traceId } : {}),
  });
}

/**
 * Публичный API-вызов с refresh-retry interceptor: на 401 пробуем refresh один раз,
 * повторяем запрос; при повторной ошибке — чистим токены и пробрасываем.
 */
export async function apiRequest<T>(
  path: string,
  // biome-ignore lint/suspicious/noExplicitAny: Zod's ZodType input type is any by design
  schema: z.ZodType<T, z.ZodTypeDef, any>,
  options: RequestOptions = {},
): Promise<T> {
  try {
    return await rawRequest(path, schema, options);
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 401 && !options.skipAuth) {
      // S-FIX-9-FE §3 — natural TTL expire (15min) ловит каждого пользователя.
      // 1. Если есть refresh_token — пробуем refresh.
      if (refreshToken) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          return await rawRequest(path, schema, options);
        }
      }
      // 2. Refresh не сработал или его нет — clear tokens + redirect на /login. С-I3 Р.1 (круг 1
      // гейта): аккаунт отключён администратором — вход покажет причину, а не пустую форму.
      redirectToLogin(
        isAccountDisabled(err) ? { reason: 'account_disabled', entity_name: null } : undefined,
      );
    }
    // С-I3 Р.1 (ACC-B3) — компания запроса недоступна (членство отключили посреди сессии): общий
    // обработчик обновляет токен и перечитывает профиль, а не оставляет экран ошибок. `/auth/me`
    // разбирается сам (`fetchMe`).
    if (isEntityForbidden(err) && !options.skipAuth && path !== '/auth/me') {
      entityForbiddenHandler?.();
    }
    throw err;
  }
}

let entityForbiddenHandler: (() => void) | null = null;

/** С-I3 Р.1 — обработчик «компания запроса недоступна» (ставит провайдер приложения). */
export function onEntityForbidden(handler: (() => void) | null): void {
  entityForbiddenHandler = handler;
}

/** С-I3 Р.1 — тот же разбор по сигналу не из запроса (поток SSE закрыт с `access_revoked`). */
export function notifyEntityForbidden(): void {
  entityForbiddenHandler?.();
}

/** 401 «аккаунт отключён» (`details.code = ACCOUNT_DISABLED`), а не истёкший токен. */
function isAccountDisabled(err: ApiRequestError): boolean {
  return (err.details as { code?: unknown } | undefined)?.code === 'ACCOUNT_DISABLED';
}

/**
 * С-I3 Р.1, круг 1 гейта (UI и UX, MAJOR) — причина выхода. Без неё человек посреди работы попадал
 * на пустую форму входа, а вход с верным паролем отвечал «неверный пароль»: петля до звонка в
 * поддержку. Причина лежит в sessionStorage этой вкладки (не в адресе — ссылку с выдуманной причиной
 * не подсунуть) и читается входом один раз.
 */
export type LogoutReason = {
  /** `access_revoked` — доступ к компании отключён; `account_disabled` — отключён весь аккаунт. */
  reason: 'access_revoked' | 'account_disabled';
  entity_name: string | null;
};
const LOGOUT_REASON_KEY = 'staiter.logout_reason';

export function takeLogoutReason(): LogoutReason | null {
  try {
    const raw = sessionStorage.getItem(LOGOUT_REASON_KEY);
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { reason?: unknown; entity_name?: unknown };
    if (v.reason !== 'access_revoked' && v.reason !== 'account_disabled') return null;
    return {
      reason: v.reason,
      entity_name: typeof v.entity_name === 'string' && v.entity_name ? v.entity_name : null,
    };
  } catch {
    return null;
  }
}

/**
 * Выход на страницу входа с возвратом на текущую. `window.location.replace` — без зависимости
 * api/client → router; `?redirect=...` сохраняет место для возврата после входа.
 */
export function redirectToLogin(reason?: LogoutReason): void {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    tokenStorage.clear();
    if (reason) {
      try {
        sessionStorage.setItem(LOGOUT_REASON_KEY, JSON.stringify(reason));
      } catch {
        /* приватный режим — вход без причины, как раньше */
      }
    }
    const redirectTo = window.location.pathname + window.location.search;
    window.location.replace(`/login?redirect=${encodeURIComponent(redirectTo)}`);
  }
}

/**
 * С-I3 Р.1 (ACC-B3) — обновить токен сейчас: сервер выдаёт его на активную компанию (компания по
 * умолчанию, если членство в ней активно, иначе первое активное). `false` — активных компаний нет
 * или аккаунт отключён (пара токенов уже сброшена).
 */
export function refreshSession(): Promise<boolean> {
  return tryRefresh();
}

const RefreshResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
});

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Single-flight: параллельные 401 (истёкший access при нескольких query на
 * странице) ждут ОДИН общий refresh. Раньше каждый вызывал /auth/refresh с
 * одним и тем же refresh-токеном: первый ротировал пару, остальные детектились
 * backend'ом как replay → отзыв всей family → разлогин каждые ~15 минут.
 */
function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  // Кросс-таб: другая вкладка могла уже ротировать пару. Перечитываем storage —
  // если access сменился, повторяем запрос с ним без сетевого refresh.
  const staleAccess = accessToken;
  tokenStorage.load();
  if (accessToken && accessToken !== staleAccess) return true;
  if (!refreshToken) return false;
  try {
    const data = await rawRequest('/auth/refresh', RefreshResponse, {
      method: 'POST',
      body: { refresh_token: refreshToken },
      skipAuth: true,
    });
    tokenStorage.set(data.access_token, data.refresh_token);
    return true;
  } catch (err) {
    // Чистим пару только при однозначном отказе backend'а. Сетевой сбой / 5xx
    // не разлогинивает — токены могут быть ещё валидны.
    if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
      tokenStorage.clear();
    }
    return false;
  }
}
